import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function WorkItemLinkingPage() {
  const faqs = [
    {
      question: 'Does EvidentTrail integrate with Jira or Azure DevOps?',
      answer: 'Not directly. It captures work item identifiers that appear in GitHub metadata.'
    },
    {
      question: 'What evidence is included?',
      answer: 'Pull request metadata, commit metadata, approvals, and linked work item identifiers.'
    },
    {
      question: 'Do you store work item content?',
      answer: 'No. EvidentTrail stores identifiers only, not work item content.'
    },
    {
      question: 'Is this evidence-only?',
      answer: 'Yes. EvidentTrail does not enforce policies; it provides evidence packs.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Auditors look for evidence that every change is tied to an approved work item and that approvals occurred before merge.'
      ],
      bullets: [
        'Work item identifiers linked to PRs and commits',
        'Approval evidence with timestamps',
        'Traceability from request to merge'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Evidence packs include work item identifiers from GitHub metadata, PR approvals, and required check status.'
      ],
      bullets: [
        'Work item identifiers from PR metadata',
        'Approval evidence and reviewer identity',
        'Required check status metadata'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail reads GitHub metadata only. It does not integrate directly with external ticket systems or store work item content.'
      ],
      bullets: [
        'Read-only GitHub App access',
        'Metadata-only evidence capture',
        'Exportable packs with checksums'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use the work item identifier column to show linkage, then verify approvals and required checks for each sample.'
      ],
      bullets: [
        'Work item identifiers appear alongside PRs',
        'Approvals confirm authorization',
        'Checksums validate export integrity'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Missing work item IDs or manual screenshots without traceability lead to audit findings.'
      ],
      bullets: [
        'Work item identifiers missing in PRs',
        'Approvals not captured before merge',
        'Evidence exports without a checksum trail'
      ]
    }
  ];

  return (
    <MarketingLayout
      title="Linking Code Changes to Approved Work Items"
      description="Learn about linking code changes to approved work items for audit compliance. Business justification and authorization traceability."
      h1="Linking Code Changes to Approved Work Items"
      heroText="Many organizations require code changes to be linked to approved work items for audit traceability."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Work Item Linking', url: 'https://evidenttrail.com/work-item-linking' }
        ]}
      />
      <FaqJsonLd items={faqs} />

      {/* What Auditors Verify */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Auditors Use Linkage to Verify</h2>
            <div className={tokens.card}>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>Business justification for changes</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>Authorization prior to implementation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>Separation between request and approval</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How EvidentTrail helps (read-only evidence)</h2>
            <p className={`${tokens.text.base} mb-4`}>
              EvidentTrail captures work item identifiers from GitHub pull request and commit metadata, then links
              them to approval evidence and exportable packs.
            </p>
            <p className={`${tokens.text.base}`}>
              We do not store work item content, source code, or build logs.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>FAQs</h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.question} className={tokens.card}>
                  <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                  <p className="text-sm text-slate-300">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sampling Context */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>When Linkage is Reviewed</h2>
            <p className={`${tokens.text.base} mb-6`}>
              This linkage is often reviewed during sampling exercises.
            </p>
          </div>
        </div>
      </section>

      {/* How EvidentTrail Fits */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How EvidentTrail Supports Work Item Linking</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail captures work item references from pull requests and commits, making it possible to trace changes back to approved work items.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Topics</h2>
          <div className="grid md:grid-cols-5 gap-6">
            <Link to="/github-change-control" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">GitHub Change Control</h4>
              <p className="text-sm text-slate-400">Link approvals to change control evidence.</p>
            </Link>
            <Link to="/audit-readiness" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Audit Readiness</h4>
              <p className="text-sm text-slate-400">Prepare for sampling with evidence packs.</p>
            </Link>
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">PR Approvals Exporter</h4>
              <p className="text-sm text-slate-400">Generate approval evidence quickly.</p>
            </Link>
            <Link to="/solutions" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Solutions</h4>
              <p className="text-sm text-slate-400">Explore compliance-focused evidence workflows.</p>
            </Link>
            <Link to="/security" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Security</h4>
              <p className="text-sm text-slate-400">Read-only access and data minimization boundaries.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Learn More About Work Item Traceability</h2>
            <p className={`${tokens.text.base} mb-8`}>
              See how work item linking supports audit compliance.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/faq" className="text-slate-300 hover:text-white transition-colors">
                Security and audit FAQ →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
